import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { hasPermission } from '@/types';
import { useParams, useNavigate } from 'react-router-dom';
import { employees as api } from '@/api/endpoints';
import type {
  Employee, EmployeeMeta, EmploymentContract, ContractInput, ContractType,
  EmployeeQualification, QualificationInput, QualificationType, QualificationStatus,
} from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ArchiveFilesCard } from '@/components/ArchiveFilesCard';
import { useAuth } from '@/auth/AuthContext';
import { EmployeeModal, nearestBadge } from './KadryPage';
import { EwidencjaTab } from './EwidencjaTab';
import { UrlopyTab } from './UrlopyTab';
import { formatCurrency } from '@/lib/money';

type Tab = 'contracts' | 'qualifications' | 'files' | 'worktime' | 'leave';

export function KadryDetailPage() {
  const { id } = useParams();
  const empId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'HR_SENSITIVE');  // parytet z EmployeeApi
  const mozeUrlopy = hasPermission(user, 'HR_LEAVE_VIEW');  // parytet z LeaveApi

  const [emp, setEmp] = useState<Employee | null>(null);
  const [meta, setMeta] = useState<EmployeeMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('contracts');
  const [editEmp, setEditEmp] = useState(false);
  const [contractModal, setContractModal] = useState<EmploymentContract | 'new' | null>(null);
  const [qualModal, setQualModal] = useState<EmployeeQualification | 'new' | null>(null);

  const reload = () => {
    api.get(empId).then(setEmp).catch(e => setErr(e?.message ?? String(e)));
  };
  useEffect(() => { reload(); api.meta().then(setMeta).catch(() => {}); /* eslint-disable-next-line */ }, [empId]);

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!emp) return <div className="text-ink-400">Ładowanie…</div>;

  const onDelContract = async (c: EmploymentContract) => {
    if (!confirm('Usunąć tę umowę?')) return;
    try { await api.contracts.remove(empId, c.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };
  const onDelQual = async (q: EmployeeQualification) => {
    if (!confirm(`Usunąć „${q.name}"?`)) return;
    try { await api.qualifications.remove(empId, q.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };

  return (
    <div className="max-w-[1100px]">
      <button onClick={() => navigate('/kadry')} className="btn btn-ghost btn-sm mb-3 text-ink-500">
        <Icon name="arrow" className="w-3.5 h-3.5 rotate-180"/> Kadry
      </button>

      <header className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="mb-1">{emp.fullName}</h1>
          <div className="text-ink-500 text-[13px]">
            {emp.position}{emp.department ? ' · ' + emp.department : ''}
            <span className="ml-2 pill" style={{ background: '#eef2ff' }}>{emp.employmentStatusName}</span>
          </div>
          <div className="text-ink-400 text-[12px] mt-1">
            {[emp.phone, emp.email, emp.userName ? 'konto: ' + emp.userName : null].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        {canEdit && (
          <button onClick={() => setEditEmp(true)} className="btn btn-secondary btn-sm">
            <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj dane
          </button>
        )}
      </header>

      {/* Karta danych podstawowych */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-5">
        <div className="card"><div className="card-body text-[13px] grid grid-cols-2 gap-y-1.5">
          <KV k="Zatrudniony od" v={emp.hireDate}/>
          <KV k="Data urodzenia" v={emp.birthDate}/>
          {emp.pesel != null && <KV k="PESEL" v={emp.pesel}/>}
          <KV k="Adres" v={[emp.street, [emp.postalCode, emp.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null}/>
          <KV k="Kontakt awaryjny" v={emp.emergencyContact}/>
          <KV k="Firma" v={emp.companyName}/>
        </div></div>
        {emp.notes && <div className="card"><div className="card-body text-[13px] text-ink-600 whitespace-pre-wrap">{emp.notes}</div></div>}
      </div>

      {/* Zakładki */}
      <div className="flex gap-1 mb-3 border-b border-ink-100">
        {([['contracts', 'Umowy', emp.contracts.length], ['qualifications', 'Kwalifikacje', emp.qualifications.length], ['worktime', 'Ewidencja / Nadgodziny', null], ...(mozeUrlopy ? [['leave', 'Urlopy', null] as [Tab, string, number | null]] : []), ['files', 'Pliki', null]] as [Tab, string, number | null][]).map(([t, label, count]) => (
          <button key={t} onClick={() => setTab(t)}
            className={'px-3 py-2 text-[13px] border-b-2 -mb-px ' + (tab === t ? 'border-water-500 text-ink-900 font-semibold' : 'border-transparent text-ink-500 hover:text-ink-700')}>
            {label}{count != null ? ` (${count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'contracts' && (
        <div className="card"><div className="card-body !p-0">
          <div className="flex items-center px-4 py-2 border-b border-ink-100">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500">Umowy</span>
            {canEdit && <button onClick={() => setContractModal('new')} className="ml-auto btn btn-secondary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj umowę</button>}
          </div>
          {emp.contracts.length === 0 ? <div className="p-5 text-center text-ink-400 text-[12px]">Brak umów.</div> : (
            <table className="table-hw">
              <thead><tr><th>Rodzaj</th><th className="w-28">Od</th><th className="w-36">Do</th><th>Etat</th><th>Wynagrodzenie</th><th className="w-20"></th></tr></thead>
              <tbody>
                {emp.contracts.map(c => (
                  <tr key={c.id}>
                    <td><div className="font-semibold text-ink-900 text-[13px]">{c.typeName}</div>{c.position && <div className="text-[11px] text-ink-400">{c.position}</div>}</td>
                    <td className="font-mono text-[12px]">{c.startDate}</td>
                    <td className="font-mono text-[12px]">
                      {c.endDate ? <div className="flex flex-col leading-tight"><span>{c.endDate}</span>{nearestBadge(c.daysUntil, c.urgency)}</div> : <span className="text-ink-400">bezterminowa</span>}
                    </td>
                    <td className="text-[12px]">{c.fte != null ? c.fte.toFixed(2) : '—'}</td>
                    <td className="text-[12px]">{formatCurrency(c.wage)}</td>
                    <td>{canEdit && <div className="flex gap-1 justify-end">
                      <button onClick={() => setContractModal(c)} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600"><Icon name="edit" className="w-3.5 h-3.5"/></button>
                      <button onClick={() => onDelContract(c)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>
                    </div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
      )}

      {tab === 'qualifications' && (
        <div className="card"><div className="card-body !p-0">
          <div className="flex items-center px-4 py-2 border-b border-ink-100">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500">Badania, szkolenia, uprawnienia</span>
            {canEdit && <button onClick={() => setQualModal('new')} className="ml-auto btn btn-secondary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj</button>}
          </div>
          {emp.qualifications.length === 0 ? <div className="p-5 text-center text-ink-400 text-[12px]">Brak wpisów.</div> : (
            <table className="table-hw">
              <thead><tr><th className="w-32">Rodzaj</th><th>Nazwa</th><th className="w-40">Ważne do</th><th className="w-24">Status</th><th className="w-20"></th></tr></thead>
              <tbody>
                {emp.qualifications.map(q => (
                  <tr key={q.id}>
                    <td className="text-[12px] text-ink-600">{q.typeName}</td>
                    <td><div className="font-semibold text-ink-900 text-[13px]">{q.name}</div>{q.issuer && <div className="text-[11px] text-ink-400">{q.issuer}</div>}</td>
                    <td className="font-mono text-[12px]">
                      {q.validUntil ? <div className="flex flex-col leading-tight"><span>{q.validUntil}</span>{nearestBadge(q.daysUntil, q.urgency)}</div> : <span className="text-ink-400">bezterminowe</span>}
                    </td>
                    <td><span className="pill" style={{ background: q.statusColor === 'green' ? '#dcfce7' : q.statusColor === 'red' ? '#fee2e2' : '#f1f5f9', color: q.statusColor === 'green' ? '#15803d' : q.statusColor === 'red' ? '#b91c1c' : '#64748b' }}>{q.statusName}</span></td>
                    <td>{canEdit && <div className="flex gap-1 justify-end">
                      <button onClick={() => setQualModal(q)} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600"><Icon name="edit" className="w-3.5 h-3.5"/></button>
                      <button onClick={() => onDelQual(q)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>
                    </div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
      )}

      {tab === 'worktime' && <EwidencjaTab employeeId={emp.id} canEdit={canEdit} meta={meta}/>}

      {tab === 'leave' && <UrlopyTab employeeId={emp.id}/>}

      {tab === 'files' && <ArchiveFilesCard scope="employee" scopeId={emp.id} scopeName={emp.fullName}/>}

      {editEmp && <EmployeeModal item={emp} onClose={() => setEditEmp(false)} onSaved={() => { setEditEmp(false); reload(); }}/>}
      {contractModal && meta && (
        <ContractModal employeeId={empId} item={contractModal === 'new' ? null : contractModal} meta={meta} canSensitive={canEdit}
          onClose={() => setContractModal(null)} onSaved={() => { setContractModal(null); reload(); }}/>
      )}
      {qualModal && meta && (
        <QualificationModal employeeId={empId} item={qualModal === 'new' ? null : qualModal} meta={meta}
          onClose={() => setQualModal(null)} onSaved={() => { setQualModal(null); reload(); }}/>
      )}
    </div>
  );
}

function KV({ k, v }: { k: string; v?: string | null }) {
  return <><div className="text-ink-400">{k}</div><div className="text-ink-800">{v || '—'}</div></>;
}

function ContractModal({ employeeId, item, meta, canSensitive, onClose, onSaved }: {
  employeeId: number; item: EmploymentContract | null; meta: EmployeeMeta; canSensitive: boolean;
  onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!item;
  const [type, setType] = useState<ContractType>(item?.type ?? 'UOP_INDEFINITE');
  const [startDate, setStartDate] = useState(item?.startDate ?? new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(item?.endDate ?? '');
  const [position, setPosition] = useState(item?.position ?? '');
  const [fte, setFte] = useState<string>(item?.fte != null ? String(item.fte) : '');
  const [wage, setWage] = useState<string>(item?.wage != null ? String(item.wage) : '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const payload: ContractInput = {
      type, startDate, endDate: endDate || null, position: position.trim() || null,
      fte: fte === '' ? null : Number(fte),
      wage: canSensitive ? (wage === '' ? null : Number(wage)) : undefined,
      notes: notes.trim() || null,
    };
    try {
      if (isEdit) await api.contracts.update(employeeId, item!.id, payload);
      else await api.contracts.add(employeeId, payload);
      onSaved();
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  return (
    <ModalShell title={isEdit ? 'Edytuj umowę' : 'Nowa umowa'} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ type, startDate, endDate, position, fte, wage, notes }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Rodzaj *</span>
          <SearchableSelect value={type} onChange={v => setType(v as ContractType)}>
            {meta.contractTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SearchableSelect></label>
        <label className="field"><span className="field-label">Stanowisko</span>
          <input value={position} onChange={e => setPosition(e.target.value)} className="input"/></label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Od *</span>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Do (puste = bezterminowa)</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input"/></label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Etat (np. 1.00)</span>
          <input type="number" min="0" step="0.25" value={fte} onChange={e => setFte(e.target.value)} className="input"/></label>
        {canSensitive && (
          <label className="field"><span className="field-label">Wynagrodzenie (PLN)</span>
            <input type="number" min="0" step="0.01" value={wage} onChange={e => setWage(e.target.value)} className="input"/></label>
        )}
      </div>
      <label className="field"><span className="field-label">Notatki</span>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>
    </ModalShell>
  );
}

function QualificationModal({ employeeId, item, meta, onClose, onSaved }: {
  employeeId: number; item: EmployeeQualification | null; meta: EmployeeMeta;
  onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!item;
  const [type, setType] = useState<QualificationType>(item?.type ?? 'MEDICAL');
  const [name, setName] = useState(item?.name ?? '');
  const [issuer, setIssuer] = useState(item?.issuer ?? '');
  const [issueDate, setIssueDate] = useState(item?.issueDate ?? '');
  const [validFrom, setValidFrom] = useState(item?.validFrom ?? '');
  const [validUntil, setValidUntil] = useState(item?.validUntil ?? '');
  const [remindDaysBefore, setRemindDaysBefore] = useState<number>(item?.remindDaysBefore ?? 30);
  const [status, setStatus] = useState<QualificationStatus>(item?.status ?? 'VALID');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Nazwa jest wymagana'); return; }
    setBusy(true); setErr(null);
    const payload: QualificationInput = {
      type, name: name.trim(), issuer: issuer.trim() || null,
      issueDate: issueDate || null, validFrom: validFrom || null, validUntil: validUntil || null,
      remindDaysBefore: Number(remindDaysBefore) || 30, status, notes: notes.trim() || null,
    };
    try {
      if (isEdit) await api.qualifications.update(employeeId, item!.id, payload);
      else await api.qualifications.add(employeeId, payload);
      onSaved();
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  return (
    <ModalShell title={isEdit ? 'Edytuj wpis' : 'Nowe badanie / szkolenie / uprawnienie'} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ type, name, issuer, issueDate, validFrom, validUntil, remindDaysBefore, status, notes }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Rodzaj *</span>
          <SearchableSelect value={type} onChange={v => setType(v as QualificationType)}>
            {meta.qualificationTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SearchableSelect></label>
        <label className="field"><span className="field-label">Status</span>
          <SearchableSelect value={status} onChange={v => setStatus(v as QualificationStatus)}>
            {meta.qualificationStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SearchableSelect></label>
      </div>
      <label className="field"><span className="field-label">Nazwa *</span>
        <input value={name} onChange={e => setName(e.target.value)} className="input"
               placeholder="np. Badanie okresowe / Szkolenie BHP okresowe / Prawo jazdy kat. C+E / UDT operator HDS"/></label>
      <label className="field"><span className="field-label">Wydający / instytucja</span>
        <input value={issuer} onChange={e => setIssuer(e.target.value)} className="input"/></label>
      <div className="grid grid-cols-3 gap-3">
        <label className="field"><span className="field-label">Data wydania</span>
          <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Ważne od</span>
          <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Ważne do</span>
          <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="input"/></label>
      </div>
      <label className="field w-40"><span className="field-label">Przypominaj (dni przed)</span>
        <input type="number" min="0" step="1" value={remindDaysBefore} onChange={e => setRemindDaysBefore(Number(e.target.value))} className="input"/></label>
      <label className="field"><span className="field-label">Notatki</span>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>
    </ModalShell>
  );
}

/** Wspólna otoczka modala (nagłówek + body + stopka z przyciskami). */
export function ModalShell({ title, onClose, onSubmit, busy, err, children, draftValue }: {
  title: string; onClose: () => void; onSubmit: (e: React.FormEvent) => void;
  busy: boolean; err: string | null; children: React.ReactNode; draftValue: unknown;
}) {
  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: draftValue, onClose, busy,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <form onClick={e => e.stopPropagation()} onSubmit={onSubmit}
            className="bg-white rounded-lg shadow-xl w-full max-w-[640px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">{title}</h2></div>
        <div className="p-5 grid gap-3">
          {children}
          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
          <div className="flex justify-end gap-2 mt-1">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
